import { fetchCatalogue, getTranslation, registerTranslations, stopWaitingForCatalogues, type AppLocale } from '@floorfile/i18n'

/**
 * The background fill-in of a language (the French split, 2026-09-25), called
 * by `loadCatalogues` in `locale-catalogues.ts` once the first screen is up.
 */

/** At most this many catalogue requests at once. */
const BACKGROUND_CONCURRENCY = 6

/** Resolves once the page has had a moment to fetch what it is actually showing. */
function afterFirstScreen(): Promise<void> {
  return new Promise((resolve) => {
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback
    if (idle) idle(() => resolve(), { timeout: 2000 })
    else setTimeout(resolve, 500)
  })
}

/**
 * Fetch every catalogue of `locale` in `namespaces` that no screen has already
 * waited for, and register them in ONE pass (`registerTranslations`): each
 * registration re-renders every `useCopy` consumer, and fifty of them spread
 * over fifty microtasks would be fifty renders of the whole tree.
 *
 * Rejects if ANY catalogue failed — after registering every one that arrived —
 * which is the honest signal: `I18nProvider` turns it into `catalogueStatus:
 * 'unavailable'`, the switcher says the language needs a connection, and the
 * screens whose words did arrive keep them (D-L6 obligation 4).
 */
export async function fillCatalogues(locale: AppLocale, namespaces: readonly string[]): Promise<void> {
  await afterFirstScreen()
  // From here on nothing suspends for words: the page is up, and hiding it
  // for a fetch would take focus with it (see `stopWaitingForCatalogues`).
  stopWaitingForCatalogues()
  const wanted = namespaces.filter((namespace) => !getTranslation(namespace, locale))
  const arrived: { namespace: string; locale: AppLocale; translation: Record<string, unknown> }[] = []
  let failed = 0
  let next = 0
  const worker = async () => {
    while (next < wanted.length) {
      const namespace = wanted[next++] as string
      try {
        const translation = await fetchCatalogue(namespace, locale)
        if (translation) arrived.push({ namespace, locale, translation })
      } catch {
        failed += 1
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(BACKGROUND_CONCURRENCY, wanted.length) }, worker))
  // A screen may have waited for (and registered) one of these meanwhile.
  registerTranslations(arrived.filter((item) => !getTranslation(item.namespace, item.locale)))
  if (failed > 0) throw new Error(`${failed} ${locale} catalogue(s) could not be fetched`)
}
